import { useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import CurrentUserContext from 'contexts/CurrentUserContext';
import EntityDefinitionsContext from 'contexts/EntityDefinitionsContext';
import useGetFieldLabel from 'hooks/useGetFieldLabel';
import AccountPickerContext from 'contexts/AccountPickerContext';
import getIdentifier from 'getIdentifier';
import getTimeStamp from 'getTimeStamp';
import STORE from 'store';

import { useEntityValues } from 'screens/EntityDetailsShow/SelectedEntityProvider/useEntityOptions';
import { useBuildEntityLink } from '../../../components/GlobalSearch/hooks/useEntityLink';
import usePopup from '../../../hooks/usePopup';

const useOnEntityLinkClicked = (entity: EntityDetails.Entity) => {
  const currentUser = useContext(CurrentUserContext);
  const buildEntityLink = useBuildEntityLink(entity);

  const { values: entityValues, isLoading: isLoadingEntityValues } =
    useEntityValues(entity);
  const {
    isLoading: isLoadingEntityDefinitions,
    entityDetailsApps,
    localUpdateOrAddApp,
  } = useContext(EntityDefinitionsContext);
  const { getFieldLabel } = useGetFieldLabel();
  const { selectedAccountId } = useContext(AccountPickerContext);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const {
    isOpen: isManageEntityAppsOpen,
    open: openManageEntityApps,
    close: closeManageEntityApps,
  } = usePopup();

  const navigateToApp = useCallback(
    (app: EntityDetails.App) => {
      const link = buildEntityLink(entityValues[0], app);
      if (link) {
        navigate(link);
      } else {
        alert('Something went wrong!');
      }
    },
    [buildEntityLink, entityValues, navigate],
  );

  const isReady = useMemo(() => {
    if (
      isLoadingEntityValues ||
      entityValues.length === 0 ||
      isLoadingEntityDefinitions
    ) {
      return false;
    }

    return true;
  }, [entityValues.length, isLoadingEntityDefinitions, isLoadingEntityValues]);

  const onClick = useCallback(async () => {
    if (isLoading) {
      return;
    }
    if (!isReady) {
      alert('Something went wrong');
      return;
    }
    const apps = entityDetailsApps.filter((a) => a.entityId === entity.id);
    if (apps.length > 1) {
      openManageEntityApps();
      return;
    }

    setIsLoading(true);
    const defaultApp = apps.find((a) => entity.defaultAppId === a.id);
    const app = defaultApp ? defaultApp : apps.at(0);
    if (!app) {
      const newApp: EntityDetails.App = {
        id: getIdentifier(),
        entityId: entity.id,
        tabs: [],
        updatedOn: getTimeStamp(),
        createdOn: getTimeStamp(),
        updatedBy: currentUser.id,
        createdBy: currentUser.id,
        title: `${getFieldLabel({
          field: entity.primaryField,
          dataType: entity.entityDataset,
        })} Details`,
        urlSlug: entity.primaryField,
      };

      await STORE.contentDistributions
        .getEntityDetailsApps({
          accountId: selectedAccountId,
        })
        .doc(newApp.id)
        .set(newApp);
      localUpdateOrAddApp(newApp);

      const newEntity: EntityDetails.Entity = {
        ...entity,
        defaultAppId: newApp.id,
      };
      await STORE.getEntityDefinitionsRef({ accountId: selectedAccountId })
        .doc(entity.id)
        .set(newEntity);

      navigateToApp(newApp);
      return;
    }

    navigateToApp(app);
    setIsLoading(false);
  }, [
    isLoading,
    isReady,
    entityDetailsApps,
    navigateToApp,
    entity,
    openManageEntityApps,
    currentUser.id,
    getFieldLabel,
    selectedAccountId,
    localUpdateOrAddApp,
  ]);

  return {
    isLoading: isLoadingEntityValues || isLoadingEntityDefinitions,
    hasNoEntities: entityValues.length === 0 && !isLoadingEntityDefinitions,
    onClick: isReady ? onClick : undefined,
    isManageEntityAppsOpen,
    closeManageEntityApps,
  };
};

export default useOnEntityLinkClicked;
